import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { useBondEndorsementDialogState } from "../../hooks/useBondEndorsementDialogState";
import useBondEndorsementSubmitHandlers from "../../hooks/useBondEndorsementSubmitHandlers";
import useBondCancelDeleteHandlers from "../../hooks/useBondCancelDeleteHandlers";
import { computeNameChangeOk } from "../helpers/lawFirmBondsHelpers";
import {
  endorsePageDwbFieldsOk,
  computeDwbOk,
  isInternalReleaseRiderDisabled,
  isLawFirmEndorsementSubmitDisabled,
  LAW_FIRM_BONDS_MODES,
} from "../helpers/lawFirmBondsPageConfig";

/** Cancel / endorse / cant-find / team delete dialog state & handlers for law-firm bonds page. */
export default function useLawFirmBondsDialogsBase(mode, { filters, lawFirms, lawfirmId, fetchInitialData }) {
  const userdetails = useSelector((state) => state.auth.user);

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedBondToCancel, setSelectedBondToCancel] = useState(null);
  const [cancellationFile, setCancellationFile] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedBondToDelete, setSelectedBondToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    role,
    endorsementDialogOpen,
    setEndorsementDialogOpen,
    selectedBondToEndorse,
    setSelectedBondToEndorse,
    endorsementType,
    setEndorsementType,
    newBondAmount,
    setNewBondAmount,
    endorsementFile,
    setEndorsementFile,
    isSubmittingEndorsement,
    setIsSubmittingEndorsement,
    contactName,
    setContactName,
    contactEmail,
    setContactEmail,
    updatedName,
    setUpdatedName,
    counties,
    endorsementToCounty,
    setEndorsementToCounty,
    loadingCounties,
    estimatedAssets,
    setEstimatedAssets,
    dwbFirstName,
    setDwbFirstName,
    dwbMiddleName,
    setDwbMiddleName,
    dwbLastName,
    setDwbLastName,
    dwbSuffix,
    setDwbSuffix,
    dwbAlias,
    setDwbAlias,
  } = useBondEndorsementDialogState();

  const [cantFindDialogOpen, setCantFindDialogOpen] = useState(false);
  const [cfBondNumber, setCfBondNumber] = useState("");
  const [cfBondType, setCfBondType] = useState(null);
  const [cfName, setCfName] = useState("");
  const [cfPrincipalName, setCfPrincipalName] = useState("");
  const [cfAction, setCfAction] = useState("cancellation");
  const [cfOtherInfo, setCfOtherInfo] = useState("");
  const [cfSubmitting, setCfSubmitting] = useState(false);

  const { handleReleaseRider: handleReleaseRiderEndorsement, handleSubmitEndorsementRequest: handleSubmitEndorsement } =
    useBondEndorsementSubmitHandlers({
      setIsSubmittingEndorsement,
      setEndorsementDialogOpen,
      selectedBondToEndorse,
      endorsementType,
      endorsementFile,
      contactName,
      contactEmail,
      newBondAmount,
      estimatedAssets,
      endorsementToCounty,
      updatedName,
      dwbFirstName,
      dwbMiddleName,
      dwbLastName,
      dwbSuffix,
      dwbAlias,
      role,
      refreshData: fetchInitialData,
    });

  const { handleCancelBondSubmit, handleDeleteClose, handleDeleteConfirm, handleReviewBond, handleCantFindBondSubmit } =
    useBondCancelDeleteHandlers({
      selectedBondToCancel,
      setCancelDialogOpen,
      cancellationFile,
      setIsCancelling,
      missingCancellationFileMessage: "Please upload a document to cancel this bond.",
      selectedBondToDelete,
      setDeleteDialogOpen,
      setSelectedBondToDelete,
      setIsDeleting,
      refreshData: fetchInitialData,
      cantFind: {
        userdetails,
        filters,
        lawFirms,
        lawfirmId,
        cfBondNumber,
        cfBondType,
        cfName,
        cfPrincipalName,
        cfAction,
        cfOtherInfo,
        setCfSubmitting,
        setCantFindDialogOpen,
        setCfBondNumber,
        setCfBondType,
        setCfName,
        setCfPrincipalName,
        setCfAction,
        setCfOtherInfo,
      },
    });

  useEffect(() => {
    if (mode !== LAW_FIRM_BONDS_MODES.CANCEL || !endorsementDialogOpen) return;
    setDwbFirstName("");
    setDwbMiddleName("");
    setDwbLastName("");
    setDwbSuffix("");
    setDwbAlias("");
  }, [mode, endorsementDialogOpen]);

  const nameChangeOk = computeNameChangeOk({
    endorsementType,
    endorsementToCounty,
    updatedName,
    selectedBondToEndorse,
    dwbFirstName,
    dwbLastName,
  });

  const dwbOk = computeDwbOk(
    endorsementType,
    selectedBondToEndorse,
    updatedName,
    dwbFirstName,
    dwbLastName
  );

  const endorseInlineDwbOk = endorsePageDwbFieldsOk(
    selectedBondToEndorse,
    updatedName,
    dwbFirstName,
    dwbLastName
  );

  const internalReleaseDisabled = isInternalReleaseRiderDisabled(mode, {
    selectedBondToEndorse,
    isSubmittingEndorsement,
    endorsementType,
    nameChangeOk,
    dwbOk,
    endorseInlineDwbOk,
  });

  const lawFirmEndorseSubmitDisabled = isLawFirmEndorsementSubmitDisabled(mode, {
    isSubmittingEndorsement,
    endorsementType,
    endorsementFile,
    endorsementToCounty,
    nameChangeOk,
    dwbOk,
    endorseInlineDwbOk,
    selectedBondToEndorse,
  });

  return {
    cancelDialogOpen,
    setCancelDialogOpen,
    selectedBondToCancel,
    setSelectedBondToCancel,
    cancellationFile,
    setCancellationFile,
    isCancelling,
    handleCancelBondSubmit,
    deleteDialogOpen,
    setDeleteDialogOpen,
    selectedBondToDelete,
    setSelectedBondToDelete,
    isDeleting,
    handleDeleteClose,
    handleDeleteConfirm,
    handleReviewBond,
    endorsementDialogOpen,
    setEndorsementDialogOpen,
    selectedBondToEndorse,
    setSelectedBondToEndorse,
    endorsementType,
    setEndorsementType,
    newBondAmount,
    setNewBondAmount,
    endorsementFile,
    setEndorsementFile,
    isSubmittingEndorsement,
    role,
    contactName,
    setContactName,
    contactEmail,
    setContactEmail,
    updatedName,
    setUpdatedName,
    counties,
    endorsementToCounty,
    setEndorsementToCounty,
    loadingCounties,
    estimatedAssets,
    setEstimatedAssets,
    dwbFirstName,
    setDwbFirstName,
    dwbMiddleName,
    setDwbMiddleName,
    dwbLastName,
    setDwbLastName,
    dwbSuffix,
    setDwbSuffix,
    dwbAlias,
    setDwbAlias,
    cantFindDialogOpen,
    setCantFindDialogOpen,
    cfBondNumber,
    setCfBondNumber,
    cfBondType,
    setCfBondType,
    cfName,
    setCfName,
    cfPrincipalName,
    setCfPrincipalName,
    cfAction,
    setCfAction,
    cfOtherInfo,
    setCfOtherInfo,
    cfSubmitting,
    nameChangeOk,
    internalReleaseDisabled,
    lawFirmEndorseSubmitDisabled,
    handleCantFindBondSubmit,
    handleSubmitEndorsement,
    handleReleaseRiderEndorsement,
  };
}
